import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Loader from '../../components/Loader';
import { LDFlags } from '../../constants/flags';
import { HOME } from '../../constants/urls';
import { useAuthenticateAnonymousCustomerMutation } from '../../gql/generated/graphql';
import { useFlag, useStep, useTempInfo } from '../../hooks';
import { CookieKeys, useCookie } from '../../hooks/useCookie';
import { logger } from '../../services/sentry';
import { resetAuth } from '../../utils/auth';
import EndOfFlowAnimation from './EndOfFlowAnimation';
import useNextSteps from './useNextSteps';

const NextSteps = () => {
  const history = useHistory();
  const step = useStep();
  const {
    importInfo,
    loading: nextStepsLoading,
    wantsToSell,
    marketplaceDashboard,
  } = useNextSteps();
  const { info, infoLoading } = useTempInfo({
    fetchPolicy: 'network-only',
    refresh: true,
  });
  const [importInfoDone, setImportInfoDone] = useState(false);

  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [accessToken, setAccessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const passwordlessDashboardAccess = useFlag(LDFlags.PASSWORDLESS_DASHBOARD_ACCESS);
  const [authenticateAnonymousCustomer] = useAuthenticateAnonymousCustomerMutation();

  history.listen(() => {
    if (history.action === 'POP') {
      history.push(HOME);
    }
  });

  const alreadyRan = useRef(false);
  useEffect(() => {
    const importTempInfo = async () => {
      if (info && !nextStepsLoading && !alreadyRan.current) {
        alreadyRan.current = true;
        await importInfo();
        setImportInfoDone(true);
      }
    };

    importTempInfo();
  }, [info, nextStepsLoading]);

  const handleNext = async () => {
    if (passwordlessDashboardAccess && !accessToken && guid) {
      try {
        const accessTokenResult = await authenticateAnonymousCustomer({
          variables: {
            tempInfoId: guid,
          },
        });

        if (!accessTokenResult.data?.authenticateAnonymousCustomer) {
          throw new Error('Failed to authenticate anonymous customer');
        }

        setAccessToken(accessTokenResult.data.authenticateAnonymousCustomer);
        resetAuth({ removeGuid: true });
        step.moveNext({ passwordlessDashboardAccess }, true);
        return;
      } catch (error) {
        logger.error('NextSteps.tsx', (error as Error).message, null, error);
      }
    }

    if (accessToken && passwordlessDashboardAccess) {
      resetAuth({ removeGuid: true });
    }
    step.moveNext(
      {
        passwordlessDashboardAccess: !!accessToken && passwordlessDashboardAccess,
      },
      true,
    );
  };

  const showLoader = wantsToSell && !marketplaceDashboard;
  const isMarketplaceSeller = wantsToSell && marketplaceDashboard;

  // Marketplace sellers skip the end-of-flow animation and land on the live
  // dashboard as soon as their imported info resolves.
  const hasNavigated = useRef(false);
  useEffect(() => {
    if (isMarketplaceSeller && importInfoDone && !hasNavigated.current) {
      hasNavigated.current = true;
      handleNext();
    }
  }, [isMarketplaceSeller, importInfoDone]);

  if (infoLoading || nextStepsLoading || showLoader || isMarketplaceSeller) {
    return <Loader />;
  }

  return <EndOfFlowAnimation importInfoDone={importInfoDone} onNext={handleNext} />;
};

export default NextSteps;
